/**
 * Campaign tags (utm_*) from social posts and ads, kept for the visit so the
 * sign-up it leads to can say where it came from. Stored per tab in
 * sessionStorage; every read and write is guarded (private windows, blocked
 * storage) and the site works the same without it.
 */

const KEY = "rai.utm";
export const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"] as const;
export type Utm = Partial<Record<(typeof UTM_FIELDS)[number], string>>;

const MAX_LEN = 100;

function clean(v: string | null): string | undefined {
  const s = (v ?? "").trim().slice(0, MAX_LEN);
  return s ? s : undefined;
}

/** Read utm_* (and ref) from a query string; the first tagged visit of a tab wins. */
export function captureUtm(search: string = typeof location === "undefined" ? "" : location.search): Utm {
  const params = new URLSearchParams(search);
  const found: Utm = {};
  for (const f of UTM_FIELDS) {
    const v = clean(params.get(f));
    if (v) found[f] = v;
  }
  if (Object.keys(found).length) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(found));
    } catch {
      /* storage blocked: the tags only live in this URL */
    }
    return found;
  }
  return storedUtm();
}

export function storedUtm(): Utm {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Utm = {};
    for (const f of UTM_FIELDS) {
      const v = parsed[f];
      if (typeof v === "string" && v) out[f] = v.slice(0, MAX_LEN);
    }
    return out;
  } catch {
    return {};
  }
}

/** "instagram/social/launch" from the captured tags, or undefined without any. */
export function sourceTag(utm: Utm = storedUtm()): string | undefined {
  const parts = [utm.utm_source ?? utm.ref, utm.utm_medium, utm.utm_campaign].filter(Boolean);
  return parts.length ? parts.join("/").slice(0, 200) : undefined;
}

/** Search params for a /signup link: `extra` (e.g. role) plus `source` when the visit was tagged. */
export function signupSearch(extra: Record<string, string> = {}): Record<string, string> {
  // Read the current URL too, so a CTA on the landing page itself is tagged.
  const source = sourceTag(captureUtm());
  return source ? { ...extra, source } : { ...extra };
}

/** "/signup?role=ra&source=..." for links that take a plain path. */
export function signupHref(extra: Record<string, string> = {}): string {
  const q = new URLSearchParams(signupSearch(extra)).toString();
  return q ? `/signup?${q}` : "/signup";
}
